import { defineStore } from 'pinia';
import { ref } from 'vue';
import { config } from '../utils/config';
import { generateUUID } from '../utils/uuid';

interface Credentials {
  username: string;
  password: string;
}

export const useSessionStore = defineStore('session', () => {
  // State
  const userId = ref<string>('');
  const conversationId = ref<string>('');
  const isAuthenticated = ref(false);
  const userAttributes = ref<Record<string, string>>({});

  // Actions
  const login = async (widgetId: string, credentials: Credentials) => {
    try {
      // In a real application, this would be an API call
      const apiUrl = `${config.apiUrl}/api/auth/login`;
      console.log(`[Orbitar Chat] Logging in at: ${apiUrl}`, { widgetId, username: credentials.username });
      
      // For now, we'll simulate a successful login
      
      // Generate a unique user ID using UUID
      const mockUserId = generateUUID();
      userId.value = mockUserId;
      isAuthenticated.value = true;
      
      // Store in local storage for persistence
      localStorage.setItem(`orbitar_user_${widgetId}`, mockUserId);
      
      return true;
    } catch (error) {
      console.error('[Orbitar Chat] Login error:', error);
      return false;
    }
  };

  const checkExistingSession = async (widgetId: string) => {
    try {
      // Check local storage for existing session
      const storedUserId = localStorage.getItem(`orbitar_user_${widgetId}`);
      const storedConversationId = localStorage.getItem(`orbitar_conversation_${widgetId}`);
      
      if (storedUserId) {
        userId.value = storedUserId;
        isAuthenticated.value = true;
        
        if (storedConversationId) {
          conversationId.value = storedConversationId;
        }
        
        // In a real application, verify the session with the server
        const apiUrl = `${config.apiUrl}/api/sessions/verify`;
        console.log(`[Orbitar Chat] Verifying session at: ${apiUrl}`, { widgetId, userId: storedUserId });
        
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('[Orbitar Chat] Session check error:', error);
      return false;
    }
  };

  const initSession = async (widgetId: string) => {
    try {
      // This would typically be an API call to create a new conversation
      const apiUrl = `${config.apiUrl}/api/conversations`;
      console.log(`[Orbitar Chat] Creating new conversation at: ${apiUrl}`, { widgetId });
      
      // For now, generate a UUID for the conversation if we don't have one
      if (!conversationId.value) {
        const newConversationId = generateUUID();
        conversationId.value = newConversationId;
        
        // Store in local storage
        localStorage.setItem(`orbitar_conversation_${widgetId}`, newConversationId);
      }
      
      return conversationId.value;
    } catch (error) {
      console.error('[Orbitar Chat] Session initialization error:', error);
      throw error;
    }
  };

  const logout = (widgetId: string) => {
    // In a real application, this would make a server call
    const apiUrl = `${config.apiUrl}/api/auth/logout`;
    console.log(`[Orbitar Chat] Logging out at: ${apiUrl}`, { widgetId, userId: userId.value });
    
    // Clear session data
    userId.value = '';
    conversationId.value = '';
    isAuthenticated.value = false;
    userAttributes.value = {};
    
    // Remove from local storage
    localStorage.removeItem(`orbitar_user_${widgetId}`);
    localStorage.removeItem(`orbitar_conversation_${widgetId}`);
  };

  const setUserAttribute = (key: string, value: string) => {
    userAttributes.value[key] = value;
  };

  return {
    userId,
    conversationId,
    isAuthenticated,
    userAttributes,
    login,
    checkExistingSession,
    initSession,
    logout,
    setUserAttribute,
  };
}); 